import { FC, useState, useEffect, useRef } from 'react';
import { TextController, SelectController } from '../utils/controllers';
import { BaseForm } from '../utils/BaseForm';
import { useHUser } from '../items/hooks/HUsers';
import { RoleFormProps, FormValues } from './interfaces/IRoleForm';
import { Box, IconButton, Chip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { UseFormWatch } from 'react-hook-form';

/**
 * RoleForm dialog for adding/editing roles.
 * Handles form state, validation, and submission.
 *
 * @param {RoleFormProps} props - The props for the component.
 * @returns {JSX.Element} The rendered RoleForm dialog.
 */
const RoleForm: FC<RoleFormProps> = ({ open, onClose, onSubmit, initialRole }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addedDesks, setAddedDesks] = useState<string[]>([]);
    const watchRef = useRef<UseFormWatch<FormValues> | null>(null);
    const { rolesdesks, loading: rolesDesksLoading, fetchRolesDesks } = useHUser();

    useEffect(() => {
        fetchRolesDesks();
    }, [fetchRolesDesks]);

    // Initialize addedDesks when initialRole changes
    useEffect(() => {
        if (initialRole?.desks) {
            setAddedDesks(initialRole.desks);
        } else {
            setAddedDesks([]);
        }
    }, [initialRole]);

    const defaultValues: FormValues = {
        role: initialRole?.role || '',
        desks: '',
    };

    /**
     * Transform form values to API payload format.
     * @param {FormValues} data - The form values.
     * @returns {object} The transformed payload.
     */
    const transformData = (data: FormValues) => ({
        role: data.role.trim(),
        desks: addedDesks.join(','), // Send all added desks as comma-separated string
    });

    /**
     * Handle form submission and call onSubmit prop.
     * @param {object} transformedData - The transformed payload.
     */
    const handleSubmit = async (transformedData: any) => {
        setIsSubmitting(true);
        try {
            await onSubmit(transformedData);
        } catch (error) {
            // Error is already handled by BaseForm
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    // Extract unique desks from rolesdesks
    const uniqueDesks = Array.from(new Set(rolesdesks.map((rd: any) => rd.desk).filter(Boolean)));

    /**
     * Add the currently selected desk to the list of added desks.
     */
    const handleAddDeskClick = () => {
        if (!watchRef.current) return;

        // Get the current value using watch
        const selectedDesk = watchRef.current('desks');

        if (selectedDesk && !addedDesks.includes(selectedDesk)) {
            setAddedDesks([...addedDesks, selectedDesk]);
        }
    };

    /**
     * Remove a desk from the list of added desks.
     * @param {string} deskToRemove - The desk to remove.
     */
    const handleRemoveDesk = (deskToRemove: string) => {
        setAddedDesks(addedDesks.filter((desk) => desk !== deskToRemove));
    };

    return (
        <BaseForm
            key={initialRole?.role || 'new-role'}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title='Add/Update Role'
            defaultValues={defaultValues}
            transformData={transformData}
            loading={isSubmitting}
        >
            {(control, formState, watch) => {
                // Store watch ref for accessing form values
                watchRef.current = watch;

                return (
                    <>
                        <TextController
                            name='role'
                            control={control}
                            label='Role'
                            rules={{ required: 'Role is required' }}
                            sx={{ marginBottom: 2 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
                            <Box sx={{ flex: 1 }}>
                                <SelectController
                                    name='desks'
                                    control={control}
                                    label='Desk'
                                    options={uniqueDesks.map((desk: string) => ({ value: desk, label: desk }))}
                                    disabled={rolesDesksLoading}
                                />
                            </Box>
                            <IconButton color='primary' onClick={handleAddDeskClick} sx={{ marginTop: 1 }}>
                                <AddIcon />
                            </IconButton>
                        </Box>
                        {addedDesks.length > 0 && (
                            <Box sx={{ marginBottom: 2 }}>
                                <Typography variant='subtitle2' sx={{ marginBottom: 1 }}>
                                    Added Desks:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {addedDesks.map((desk) => (
                                        <Chip
                                            key={desk}
                                            label={desk}
                                            onDelete={() => handleRemoveDesk(desk)}
                                            color='primary'
                                            variant='outlined'
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                );
            }}
        </BaseForm>
    );
};

export default RoleForm;
